import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, QrCode, GraduationCap, FilePdf } from "@phosphor-icons/react";
import { api, BACKEND_URL } from "../lib/api";

export default function Landing() {
  const [institute, setInstitute] = useState(null);

  useEffect(() => {
    api.get("/settings/institute").then((r) => setInstitute(r.data)).catch(() => setInstitute(null));
  }, []);

  const hasInstitute = !!(institute && institute.name);

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex min-w-0 items-center gap-4" data-testid="landing-brand">
            {hasInstitute && institute.logo_base64 && (
              <img
                src={institute.logo_base64}
                alt={`${institute.name} logo`}
                data-testid="institute-logo"
                className="w-10 h-10 object-contain border border-slate-200 bg-white p-1"
              />
            )}
            {hasInstitute && (
              <div className="min-w-0 flex flex-col leading-tight break-words">
                <span className="font-display text-lg font-black tracking-tight" data-testid="institute-name">
                  {institute.name}
                </span>
                {institute.address && (
                  <span className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {institute.address.split(",")[0]}
                  </span>
                )}
              </div>
            )}
            <span
              aria-hidden={hasInstitute ? "true" : "false"}
              className={`font-display text-2xl font-black tracking-tighter ${hasInstitute ? "text-slate-300 border-l border-slate-200 pl-4 ml-1" : ""}`}
            >
              SMS<span className={hasInstitute ? "text-slate-300" : "text-[var(--sms-primary)]"}>.</span>
            </span>
          </div>
          <Link
            to="/login"
            data-testid="header-admin-login"
            className="font-mono-sms text-xs uppercase tracking-[0.2em] hover:text-[var(--sms-primary)] transition-colors"
          >
            Admin Sign In →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-24 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7 min-w-0">
            <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-6" data-testid="hero-tagline">
              {hasInstitute && institute.tagline ? institute.tagline : "Phase 01 · Registration & Onboarding"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black !tracking-normal leading-[1.05] break-words mb-8" data-testid="hero-headline">
              {hasInstitute ? (
                <>
                  Welcome to
                  <br />
                  <span className="text-[var(--sms-primary)]">{institute.name}.</span>
                </>
              ) : (
                <>
                  A registrar's
                  <br />
                  control room
                  <br />
                  <span className="text-[var(--sms-primary)]">— for every student.</span>
                </>
              )}
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mb-10" data-testid="hero-subtitle">
              {hasInstitute && institute.hero_subtitle
                ? institute.hero_subtitle
                : "Shareable registration forms, a queue for pending approvals, automatic Student IDs, and live mobile photo capture — synced to your desktop in real time."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/register"
                data-testid="cta-register"
                className="sms-btn-primary inline-flex items-center gap-2"
              >
                {hasInstitute ? `Enrol at ${institute.name.split(" ").slice(0, 2).join(" ")}` : "Apply for Admission"}
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/login"
                data-testid="cta-admin"
                className="sms-btn-secondary inline-flex items-center gap-2"
              >
                Admin Dashboard
              </Link>
              <a
                href={`${BACKEND_URL}/api/registrations/blank-form.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                download="application-blank.pdf"
                data-testid="cta-blank-form"
                className="sms-btn-secondary inline-flex items-center gap-2"
              >
                <FilePdf size={18} weight="duotone" />
                Download blank form
              </a>
            </div>
          </div>
          <figure className="md:col-span-5 min-w-0" data-testid="landing-campus-figure">
            <div className="aspect-[3/4] relative">
              <img
                src="/images/modern-skill-development-centre.webp"
                alt="Front entrance of Modern Skill Development Centre"
                data-testid="landing-campus-image"
                width="1500"
                height="2000"
                fetchPriority="high"
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
            <figcaption className="pt-4" data-testid="landing-campus-caption">
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2" data-testid="landing-campus-cohort">
                {`Cohort ${new Date().getFullYear()} · Now Open`}
              </div>
              <div className="font-display text-lg font-bold !tracking-normal" data-testid="landing-campus-name">
                Modern Skill Development Centre
              </div>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-20 grid md:grid-cols-3">
          {[
            {
              icon: ShieldCheck,
              title: "Approval Queue",
              copy: "A clean, dense queue for admins to review every applicant. Approve or reject with a single keystroke.",
            },
            {
              icon: GraduationCap,
              title: "Auto Student IDs",
              copy: "Approved applicants instantly receive a unique, collision-safe Student ID — STU-A1B2C3 format.",
            },
            {
              icon: QrCode,
              title: "Live Photo Sync",
              copy: "A QR code on desktop, a phone in your hand — capture and sync the student photo in milliseconds.",
            },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="p-10 border-r border-slate-200 last:border-r-0"
              >
                <Icon size={28} weight="duotone" className="text-[var(--sms-primary)] mb-6" />
                <div className="font-display text-2xl font-black tracking-tight mb-3">
                  {f.title}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{f.copy}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Institute contact footer */}
      {hasInstitute && (
        <section className="border-b border-slate-200 bg-slate-50" data-testid="institute-contact-footer">
          <div className="max-w-7xl mx-auto px-8 py-12 grid md:grid-cols-12 gap-8">
            <div className="md:col-span-5 flex items-start gap-4">
              {institute.logo_base64 && (
                <img
                  src={institute.logo_base64}
                  alt={`${institute.name} logo`}
                  className="w-16 h-16 object-contain border border-slate-200 bg-white p-2 shrink-0"
                />
              )}
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-2">
                  Get in touch
                </div>
                <div className="font-display text-2xl font-black tracking-tight">
                  {institute.name}
                </div>
                {institute.address && (
                  <div className="text-sm text-slate-600 mt-2 max-w-sm" data-testid="footer-address">
                    {institute.address}
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-4 space-y-3">
              {institute.phone && (
                <ContactRow label="Phone" value={institute.phone} testid="footer-phone" href={`tel:${institute.phone.replace(/\s+/g, "")}`} />
              )}
              {institute.email && (
                <ContactRow label="Email" value={institute.email} testid="footer-email" href={`mailto:${institute.email}`} />
              )}
            </div>
            <div className="md:col-span-3 space-y-3">
              {institute.gstin && (
                <ContactRow label="GSTIN" value={institute.gstin} testid="footer-gstin" mono />
              )}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div>© 2026 {hasInstitute ? `· ${institute.name}` : "· SMS Phase 01"}</div>
          <div>Built with React · FastAPI · MongoDB · WebSockets</div>
        </div>
      </footer>
    </div>
  );
}

function ContactRow({ label, value, testid, href, mono }) {
  const inner = (
    <span className={`text-slate-900 ${mono ? "font-mono-sms" : ""}`} data-testid={testid}>
      {value}
    </span>
  );
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      {href ? (
        <a href={href} className="text-slate-900 hover:text-[var(--sms-primary)] transition-colors text-sm break-all">
          {inner}
        </a>
      ) : (
        <div className="text-sm break-all">{inner}</div>
      )}
    </div>
  );
}
